import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/Pagination';
import ReplayPlayer from '@/components/ReplayPlayer';
import StreamPurchaseModal from '@/components/StreamPurchaseModal';
import { Play, Library, Lock, ShoppingCart, ArrowLeft, Clock, Video } from 'lucide-react';

const PER_PAGE = 12;

const dateLine = (iso) => {
  if (!iso) return 'Undated';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Undated';
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
};

const ReplayCard = ({ replay, onWatch, onBuy }) => (
  <div className="group flex h-full flex-col overflow-hidden rounded-xl border border-white/10
                  bg-white/[0.03] transition-colors hover:border-cyan-400/40"
       data-testid={`replay-card-${replay.id}`}>
    <button onClick={() => (replay.can_watch ? onWatch(replay) : onBuy(replay))}
            className="relative block aspect-video w-full overflow-hidden bg-slate-900"
            data-testid={`replay-thumb-${replay.id}`}>
      {replay.thumbnail ? (
        <img src={replay.thumbnail} alt={replay.title}
             className="h-full w-full object-cover transition-transform duration-500
                        group-hover:scale-[1.03]" />
      ) : (
        <span className="flex h-full w-full items-center justify-center
                         bg-gradient-to-br from-slate-800 via-slate-900 to-purple-950">
          <Video className="h-8 w-8 text-white/25" />
        </span>
      )}
      <span className="absolute inset-0 flex items-center justify-center bg-black/25
                       opacity-0 transition-opacity group-hover:opacity-100">
        <span className="flex h-12 w-12 items-center justify-center rounded-full
                         bg-white/90 text-slate-900">
          {replay.can_watch ? <Play className="h-5 w-5 translate-x-[1px]" />
                            : <Lock className="h-4 w-4" />}
        </span>
      </span>
      {replay.duration_minutes ? (
        <span className="absolute bottom-2 right-2 rounded bg-black/75 px-1.5 py-0.5
                         text-[11px] text-white/85">
          {replay.duration_minutes} min
        </span>
      ) : null}
    </button>

    <div className="flex flex-1 flex-col gap-2 p-4">
      {/* The badge row keeps its height whether or not a card has badges, so every
          title, date and button in a row lines up */}
      <div className="flex min-h-[22px] flex-wrap items-center gap-1.5">
        {replay.can_watch && replay.owned && (
          <Badge className="bg-cyan-600">Yours</Badge>
        )}
        {replay.is_free_replay && !replay.owned && (
          <Badge className="bg-emerald-600">Free to watch</Badge>
        )}
        {!replay.can_watch && replay.is_paid && (
          replay.sale_replay_percent ? (
            <Badge className="bg-emerald-600">
              ${(replay.sale_replay_price ?? 0).toFixed(2)}
              <span className="ml-1.5 line-through opacity-70">
                ${replay.replay_price || replay.live_price}
              </span>
            </Badge>
          ) : (
            <Badge className="bg-purple-600">${replay.replay_price || replay.live_price}</Badge>
          )
        )}
        {replay.how && (
          <span className="text-[11px] text-white/40">You {replay.how}</span>
        )}
      </div>
      <h3 className="text-base font-semibold leading-snug text-white line-clamp-2">
        {replay.title}
      </h3>
      <p className="flex items-center gap-1.5 text-xs text-white/40">
        <Clock className="h-3 w-3" /> {dateLine(replay.recorded_at)}
      </p>

      <div className="mt-auto pt-2">
        {replay.can_watch ? (
          <Button onClick={() => onWatch(replay)} size="sm"
                  className="w-full bg-gradient-to-r from-cyan-500 to-purple-500"
                  data-testid={`replay-watch-${replay.id}`}>
            <Play className="mr-2 h-4 w-4" /> Watch
          </Button>
        ) : replay.can_purchase ? (
          <Button onClick={() => onBuy(replay)} size="sm" variant="outline"
                  className="w-full border-purple-400/60 text-purple-200 hover:bg-purple-500/10"
                  data-testid={`replay-buy-${replay.id}`}>
            <ShoppingCart className="mr-2 h-4 w-4" />
            {replay.sale_replay_percent
              ? `Buy the replay — $${(replay.sale_replay_price ?? 0).toFixed(2)} (${replay.sale_replay_percent}% off)`
              : `Buy the replay — $${(replay.replay_price || replay.live_price).toFixed(2)}`}
          </Button>
        ) : (
          <p className="flex items-center gap-1.5 text-xs text-amber-200/80"
             data-testid={`replay-locked-${replay.id}`}>
            <Lock className="h-3 w-3" /> Gold or Platinum membership opens this
          </p>
        )}
      </div>
    </div>
  </div>
);

/** The Replay Archive — everything recorded, and the member's own shelf of what they hold. */
const ReplayArchivePage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') === 'library' ? 'library' : 'archive');
  const [archive, setArchive] = useState({ replays: [], total_pages: 1 });
  const [library, setLibrary] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const [buying, setBuying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, l] = await Promise.all([
        axios.get(`${API}/replays`, { params: { page, per_page: PER_PAGE } }),
        axios.get(`${API}/replays/library`),
      ]);
      setArchive(a.data);
      setLibrary(l.data.replays || []);
    } catch (e) {
      toast.error('The archive could not be loaded');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const shown = tab === 'archive' ? archive.replays : library;

  return (
    <div className="mx-auto max-w-6xl">
      <button onClick={() => navigate('/dashboard/live-stream')}
              className="mb-4 inline-flex items-center gap-1.5 text-xs text-white/45
                         transition-colors hover:text-white"
              data-testid="replays-back-to-streams">
        <ArrowLeft className="h-3.5 w-3.5" /> Live Stream
      </button>

      <div className="mb-8">
        <h1 className="mb-3 text-4xl font-bold text-white sm:text-5xl">Replay Archive</h1>
        <p className="text-base text-gray-300 md:text-lg">
          Every recording that has been kept — watch what you hold, and pick up what you missed.
        </p>
      </div>

      <div className="mb-6 flex gap-2">
        {[['archive', 'The archive', archive.total ?? archive.replays.length],
          ['library', 'My replays', library.length]].map(([key, label, count]) => (
          <button key={key} onClick={() => setTab(key)}
                  className={`rounded-full border px-4 py-1.5 text-xs transition-colors ${
                    tab === key
                      ? 'border-cyan-400/60 bg-cyan-500/10 text-cyan-100'
                      : 'border-white/15 text-white/55 hover:border-white/35'}`}
                  data-testid={`replays-tab-${key}`}>
            {label} {count ? `· ${count}` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="py-20 text-center text-white/50">Loading the archive…</p>
      ) : shown.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] py-16 text-center"
             data-testid="replays-empty">
          <Library className="mx-auto mb-4 h-12 w-12 text-white/20" />
          <p className="text-white/60">
            {tab === 'archive'
              ? 'No recordings have been posted yet. They land here as soon as they do.'
              : 'Nothing on your shelf yet. Buy a replay or reserve a seat at a live session and it will appear here.'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((replay) => (
              <ReplayCard key={replay.id} replay={replay}
                          onWatch={setPlaying} onBuy={setBuying} />
            ))}
          </div>
          {tab === 'archive' && (
            <Pagination page={page} totalPages={archive.total_pages || 1}
                        onChange={setPage} testidPrefix="replays-pagination" theme="dark" />
          )}
        </>
      )}

      {playing && (
        <ReplayPlayer replayId={playing.id} title={playing.title}
                      onClose={() => setPlaying(null)} />
      )}

      <StreamPurchaseModal
        isOpen={!!buying}
        onClose={() => setBuying(null)}
        purchaseType="replay"
        allowTypeSwitch={false}
        stream={buying ? {
          id: buying.id,
          title: buying.title,
          description: buying.description,
          live_price: buying.live_price,
          replay_price: buying.replay_price,
          sale_replay_percent: buying.sale_replay_percent,
          sale_replay_price: buying.sale_replay_price,
          scheduled_date: buying.recorded_at,
          has_replay: true,
        } : null}
        onSuccess={() => { setBuying(null); load(); }}
      />
    </div>
  );
};

export default ReplayArchivePage;
